// static/table_engine_src/core/render.js
import { escCss } from "../util/dom.js";
import { clampInt } from "../util/number.js";
import { dateParseOptionsForCol, isDateCol, normalizeDateValue } from "../util/date.js";

function isLockedCol(col) {
  return !!col?.locked;
}

export function installRender(ctx) {
  if (!ctx || typeof ctx !== "object") return;

  const { spec, table, thead, tbody, cols, colByKey, hiddenCols } = ctx;
  if (!table || !thead || !tbody || !Array.isArray(cols) || !colByKey || !hiddenCols) return;

  const on =
    typeof ctx.on === "function"
      ? ctx.on
      : (target, type, handler, options) => {
          if (!target || typeof target.addEventListener !== "function") return handler;
          if (typeof handler !== "function") return handler;
          target.addEventListener(type, handler, options);
          return handler;
        };

  const spreadsheetEnabled = spec?.spreadsheet !== false;
  const freezeLeftCols = Number.isFinite(spec?.freezeLeftCols)
    ? Math.max(0, Math.trunc(spec.freezeLeftCols))
    : 0;

  // --- row menu (opt-in)
  const rowMenuEnabled = spec?.rowMenu === true;
  const rowMenuHoverEnabled = rowMenuEnabled && spec?.rowMenuHover === true;
  const ROW_HANDLE_KEY = ctx.ROW_HANDLE_KEY || "__te_row__";
  ctx.ROW_HANDLE_KEY = ROW_HANDLE_KEY;

  // --- row virtualization (opt-in)
  const virtualSpecRaw = spec?.virtualization;
  const virtualSpec =
    virtualSpecRaw === true
      ? {}
      : (virtualSpecRaw && typeof virtualSpecRaw === "object" ? virtualSpecRaw : null);
  const virtualEnabled = !!virtualSpec;
  const virtualRowHeight = Number.isFinite(virtualSpec?.rowHeight)
    ? Math.max(16, Math.trunc(virtualSpec.rowHeight))
    : 34;
  const virtualOverscan = Number.isFinite(virtualSpec?.overscan)
    ? Math.max(0, Math.trunc(virtualSpec.overscan))
    : 10;
  const virtualRecycle = virtualEnabled && virtualSpec?.recycle !== false;
  const gridRoleEnabled = spreadsheetEnabled;

  try {
    table.setAttribute("role", gridRoleEnabled ? "grid" : "table");
    if (gridRoleEnabled) table.setAttribute("aria-multiselectable", "true");
    else table.removeAttribute("aria-multiselectable");
    if (gridRoleEnabled) table.setAttribute("aria-readonly", "false");
    else table.removeAttribute("aria-readonly");
    const explicitLabel = String(spec?.ariaLabel || "").trim();
    if (explicitLabel) table.setAttribute("aria-label", explicitLabel);
    else if (!table.getAttribute("aria-label")) table.setAttribute("aria-label", "Data grid");
  } catch {}
  try {
    thead.setAttribute("role", "rowgroup");
    tbody.setAttribute("role", "rowgroup");
    const tfoot = table.querySelector("tfoot");
    if (tfoot) tfoot.setAttribute("role", "rowgroup");
  } catch {}

  // pulled from ctx (installed earlier)
  const {
    // format
    isNumericCol,
    colScale,
    formatNumber,
    parseNumber,
    normalizeFormat,
    getDpOverride,
    clearRawDatasets,
    getAnyRawDataset,
    effectiveFormatForCol,
    setNumericInputValue,
    reformatNumericInput,
    setNumericOutputValue,
    reformatNumericOutput,

    // autosize
    hookProgrammaticValueAutosize,
    scheduleAutoWidth,

    // render notify
    scheduleNotifyRendered,
    setSuppressRenderNotify,
  } = ctx;

  const parseNum = (value, fmt = null) =>
    (typeof parseNumber === "function" ? parseNumber(value, NaN, fmt) : Number(value));

  function colFormat(col, val) {
    if (val === null || val === undefined) return "";
    if (val === "" || val === "N/A") return String(val);

    // Numeric cols: format DISPLAY = canonical * scale
    if (isNumericCol?.(col)) {
      const fmt = effectiveFormatForCol?.(col);
      const n = parseNum(val, fmt);
      if (!Number.isFinite(n)) return String(val);
      const sc = colScale?.(col) ?? 1;
      return formatNumber?.(n * sc, fmt);
    }
    return String(val);
  }

  // ---------------------------------------------------------
  // rows + selectors
  // ---------------------------------------------------------
  let _allRows = [];
  let _rowSnapshots = new WeakMap();
  let _rowMeta = new WeakMap();

  function refreshGridAriaCounts() {
    if (!gridRoleEnabled) return;
    const bodyRows = virtualEnabled ? _allRows.length : getRowEls().length;
    let rowCount = 1 + bodyRows; // + header row
    const trTotal = table.querySelector("tfoot tr.te-total-row");
    if (trTotal && String(trTotal.style?.display || "") !== "none") rowCount += 1;

    let visibleCols = 0;
    for (const c of cols) {
      if (!c?.key) continue;
      if (hiddenCols.has(c.key)) continue;
      visibleCols += 1;
    }

    try {
      table.setAttribute("aria-rowcount", String(Math.max(1, rowCount)));
      table.setAttribute("aria-colcount", String(Math.max(1, visibleCols || cols.length || 1)));
    } catch {}
  }

  function cloneDataset(ds) {
    const out = {};
    if (!ds) return out;
    for (const k in ds) out[k] = String(ds[k] ?? "");
    return out;
  }

  function captureRowSnapshot(tr) {
    const snap = { inputs: Object.create(null), outputs: Object.create(null) };
    if (!tr || tr.dataset?.teCompacted === "1") return snap;

    for (const col of cols) {
      if (!col?.key) continue;

      if (col.kind === "input") {
        const el = tr.querySelector(`[data-field="${escCss(col.key)}"]`);
        if (!el) continue;
        const ds = cloneDataset(el.dataset);
        if (ds.field == null) ds.field = col.key;
        if (ds.colKey == null) ds.colKey = col.key;
        snap.inputs[col.key] = {
          tagName: String(el.tagName || "INPUT").toUpperCase(),
          type: String(el.type || col.type || "text"),
          value: String(el.value ?? ""),
          dataset: ds,
          readOnly: !!el.readOnly,
          disabled: !!el.disabled,
          __pseudo: null,
        };
        continue;
      }

      if (col.kind === "output") {
        const el = tr.querySelector(`[data-out="${escCss(col.key)}"]`);
        if (!el) continue;
        const ds = cloneDataset(el.dataset);
        if (ds.out == null) ds.out = col.key;
        if (ds.colKey == null) ds.colKey = col.key;
        snap.outputs[col.key] = {
          tagName: "SPAN",
          textContent: String(el.textContent ?? ""),
          dataset: ds,
          __pseudo: null,
        };
      }
    }
    return snap;
  }

  function ensureRowSnapshot(tr) {
    if (!tr) return { inputs: Object.create(null), outputs: Object.create(null) };
    let snap = _rowSnapshots.get(tr);
    if (snap) return snap;

    if (tr.dataset?.teCompacted === "1") {
      snap = { inputs: Object.create(null), outputs: Object.create(null) };
      _rowSnapshots.set(tr, snap);
      return snap;
    }

    snap = captureRowSnapshot(tr);
    _rowSnapshots.set(tr, snap);
    return snap;
  }

  function rowSnapshotToItem(tr) {
    const snap = ensureRowSnapshot(tr);
    const meta = _rowMeta.get(tr) || {};
    const item = {};

    for (const col of cols) {
      if (!col?.key) continue;

      if (col.kind === "input") {
        const entry = snap.inputs[col.key];
        if (!entry) continue;
        const raw = String(entry.dataset?.raw ?? "").trim();
        if (isNumericCol?.(col) && raw !== "") item[col.key] = raw;
        else item[col.key] = String(entry.value ?? "");
        continue;
      }

      if (col.kind === "output") {
        const entry = snap.outputs[col.key];
        if (!entry) continue;
        const raw = String(entry.dataset?.raw ?? "").trim();
        if (isNumericCol?.(col) && raw !== "") item[col.key] = raw;
        else {
          const txt = String(entry.textContent ?? "");
          item[col.key] = txt.trim() === "" ? "N/A" : txt;
        }
      }
    }

    if (meta.readonly === true) item.readonly = true;
    if (meta.cell && typeof meta.cell === "object") item.__cell = meta.cell;
    if (meta.format && typeof meta.format === "object") item.__format = meta.format;
    return item;
  }

  function ensurePseudoInputEntry(tr, key) {
    const col = colByKey.get(key);
    const snap = ensureRowSnapshot(tr);
    let entry = snap.inputs[key];
    if (!entry) {
      const ds = { field: key, colKey: key };
      if (isNumericCol?.(col)) ds.teNumeric = "1";
      entry = {
        tagName: String(col?.type || "").toLowerCase() === "select" ? "SELECT" : "INPUT",
        type: String(col?.type || "text"),
        value: "",
        dataset: ds,
        readOnly: !!(col?.readonly || isLockedCol(col)),
        disabled: false,
        __pseudo: null,
      };
      snap.inputs[key] = entry;
    }

    if (!entry.dataset) entry.dataset = { field: key, colKey: key };
    if (entry.dataset.field == null) entry.dataset.field = key;
    if (entry.dataset.colKey == null) entry.dataset.colKey = key;

    if (!entry.__pseudo) {
      const pseudo = {
        tagName: String(entry.tagName || "INPUT").toUpperCase(),
        type: String(entry.type || "text"),
        dataset: entry.dataset,
        closest: () => null,
      };
      Object.defineProperty(pseudo, "value", {
        get: () => String(entry.value ?? ""),
        set: (v) => {
          entry.value = String(v ?? "");
        },
      });
      Object.defineProperty(pseudo, "readOnly", {
        get: () => !!entry.readOnly,
        set: (v) => {
          entry.readOnly = !!v;
        },
      });
      Object.defineProperty(pseudo, "disabled", {
        get: () => !!entry.disabled,
        set: (v) => {
          entry.disabled = !!v;
        },
      });
      entry.__pseudo = pseudo;
    }

    return entry.__pseudo;
  }

  function ensurePseudoOutputEntry(tr, key) {
    const snap = ensureRowSnapshot(tr);
    let entry = snap.outputs[key];
    if (!entry) {
      entry = {
        tagName: "SPAN",
        textContent: "N/A",
        dataset: { out: key, colKey: key },
        __pseudo: null,
      };
      snap.outputs[key] = entry;
    }

    if (!entry.dataset) entry.dataset = { out: key, colKey: key };
    if (entry.dataset.out == null) entry.dataset.out = key;
    if (entry.dataset.colKey == null) entry.dataset.colKey = key;

    if (!entry.__pseudo) {
      const pseudo = {
        tagName: String(entry.tagName || "SPAN").toUpperCase(),
        dataset: entry.dataset,
        closest: () => null,
      };
      Object.defineProperty(pseudo, "textContent", {
        get: () => String(entry.textContent ?? ""),
        set: (v) => {
          entry.textContent = String(v ?? "");
        },
      });
      entry.__pseudo = pseudo;
    }

    return entry.__pseudo;
  }

  function buildSnapshotFromItem(item = {}) {
    const snap = { inputs: Object.create(null), outputs: Object.create(null) };
    const rowReadonly = item?.readonly === true;

    for (const col of cols) {
      if (!col?.key) continue;

      if (col.kind === "input") {
        const spec = resolveInputRenderSpec(col, item);
        const value = item?.[col.key] ?? col.default ?? "";
        const entry = {
          tagName: spec.type0 === "select" ? "SELECT" : "INPUT",
          type: spec.type0 === "select" ? "select" : spec.inputType,
          value: value == null ? "" : String(value),
          dataset: { field: col.key, colKey: col.key },
          readOnly: false,
          disabled: false,
          __pseudo: null,
        };

        if (spec.type0 === "select") {
          if (col.readonly || isLockedCol(col) || rowReadonly) entry.disabled = true;
          snap.inputs[col.key] = entry;
          continue;
        }

        if (spec.isNumericType) {
          entry.dataset.teNumeric = "1";
          applyNumericCellFmtDataset(entry.dataset, spec.cellFmt);

          const pseudo = {
            tagName: "INPUT",
            type: entry.type,
            dataset: entry.dataset,
            value: "",
            closest: () => null,
          };
          setNumericInputValue?.(pseudo, spec.colWithFmt, value);

          entry.value = String(pseudo.value ?? "");
          entry.dataset = pseudo.dataset || entry.dataset;
          if (entry.dataset.field == null) entry.dataset.field = col.key;
          if (entry.dataset.colKey == null) entry.dataset.colKey = col.key;
        }

        if (col.key === "identifier" || col.key === "id") {
          entry.type = "text";
          try {
            delete entry.dataset.dp;
            delete entry.dataset.int;
            delete entry.dataset.strip;
            delete entry.dataset.raw;
            delete entry.dataset.teAutoFormat;
            delete entry.dataset.teCellFmt;
          } catch {}
          entry.value = value == null ? "" : String(value);
        }

        if (col.readonly || isLockedCol(col) || rowReadonly) entry.readOnly = true;
        snap.inputs[col.key] = entry;
        continue;
      }

      if (col.kind === "output") {
        const value = item?.[col.key] ?? "N/A";
        const entry = {
          tagName: "SPAN",
          textContent: value == null ? "" : String(value),
          dataset: { out: col.key, colKey: col.key },
          __pseudo: null,
        };

        if (isNumericCol?.(col) && value !== "N/A") {
          const pseudo = {
            tagName: "SPAN",
            dataset: entry.dataset,
            textContent: "",
            closest: () => null,
          };
          setNumericOutputValue?.(pseudo, col, value);
          entry.textContent = String(pseudo.textContent ?? "");
          entry.dataset = pseudo.dataset || entry.dataset;
          if (entry.dataset.out == null) entry.dataset.out = col.key;
          if (entry.dataset.colKey == null) entry.dataset.colKey = col.key;
        } else {
          entry.textContent = col?.format ? colFormat(col, value) : String(value);
        }

        snap.outputs[col.key] = entry;
      }
    }

    return snap;
  }

  function seedCompactedRowFromItem(tr, item = {}) {
    if (!virtualRecycle || !tr) return false;
    _rowSnapshots.set(tr, buildSnapshotFromItem(item));
    tr.replaceChildren();
    tr.dataset.teCompacted = "1";
    return true;
  }

  function compactRow(tr) {
    if (!virtualRecycle || !tr || tr.dataset?.teCompacted === "1") return;
    const snap = captureRowSnapshot(tr);
    _rowSnapshots.set(tr, snap);
    tr.replaceChildren();
    tr.dataset.teCompacted = "1";
  }

  function materializeRow(tr, rowIdx = null) {
    if (!virtualRecycle || !tr || tr.dataset?.teCompacted !== "1") return;
    const item = rowSnapshotToItem(tr);
    populateRowCells(tr, item);
    delete tr.dataset.teCompacted;

    if (Number.isInteger(rowIdx) && rowIdx >= 0) {
      tr.dataset.teRowIdx = String(rowIdx);
      if (rowMenuEnabled) {
        const td = tr.querySelector(`td[data-col-key="${escCss(ROW_HANDLE_KEY)}"]`);
        const badge = td ? td.querySelector(".te-row-number") : null;
        if (badge) badge.textContent = String(rowIdx + 1);
      }
    }
  }

  function getRowEls() {
    if (virtualEnabled) return _allRows;
    if (tbody && tbody.rows) return Array.from(tbody.rows);
    return [...tbody.querySelectorAll("tr")];
  }

  function getRenderedRowEls() {
    if (!virtualEnabled) return getRowEls();
    return [...tbody.querySelectorAll('tr[data-te-row="1"]')];
  }

  function getInput(tr, key) {
    if (!tr) return null;
    if (virtualRecycle && tr.dataset?.teCompacted === "1") {
      return ensurePseudoInputEntry(tr, key);
    }
    return tr.querySelector(`[data-field="${escCss(key)}"]`);
  }

  function getOut(tr, key) {
    if (!tr) return null;
    if (virtualRecycle && tr.dataset?.teCompacted === "1") {
      return ensurePseudoOutputEntry(tr, key);
    }
    return tr.querySelector(`[data-out="${escCss(key)}"]`);
  }

  function getOutputRaw(tr, key, asNumber = true) {
    const el = getOut(tr, key);
    if (!el) return asNumber ? NaN : "";
    const raw = (el.dataset && el.dataset.raw != null) ? String(el.dataset.raw) : "";
    const s = raw !== "" ? raw : String(el.textContent || "").trim();
    if (!asNumber) return s;
    let n = parseNum(s);
    if (!Number.isFinite(n) && raw === "") {
      const col = colByKey.get(key);
      const fmt = effectiveFormatForCol?.(col);
      n = parseNum(s, fmt);
    }
    return Number.isFinite(n) ? n : NaN;
  }

  // ---------------------------------------------------------
  // Row indexing + row handle badges
  //
  // Big win:
  // - Maintain O(1) row index via tr.dataset.teRowIdx
  // - Update is COALESCED (microtask) so bulk renders stay fast
  // ---------------------------------------------------------
  function updateRowHandles({ startIdx = 0, full = false } = {}) {
    const rows = getRowEls();
    const n = rows.length;
    if (!n) {
      refreshGridAriaCounts();
      return;
    }

    const start = full
      ? 0
      : Math.max(
          0,
          Math.min(n - 1, Number.isFinite(startIdx) ? Math.trunc(startIdx) : 0),
        );

    // ✅ Always stamp row index for O(1) lookup (selection glue uses this)
    for (let i = start; i < n; i++) {
      const tr = rows[i];
      try { tr.dataset.teRowIdx = String(i); } catch {}
      if (gridRoleEnabled) {
        try {
          tr.setAttribute("role", "row");
          tr.setAttribute("aria-rowindex", String(i + 2)); // +1 for 1-based, +1 for header row
        } catch {}
      }
    }

    refreshGridAriaCounts();

    if (!rowMenuEnabled) return;

    // In virtual mode, only mounted rows can contain row-handle cells.
    if (virtualEnabled) {
      const painted = getRenderedRowEls();
      painted.forEach((tr) => {
        const i = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
        if (!Number.isInteger(i) || i < 0) return;
        const td = tr.querySelector(`td[data-col-key="${escCss(ROW_HANDLE_KEY)}"]`);
        const badge = td ? td.querySelector(".te-row-number") : null;
        if (badge) badge.textContent = String(i + 1);
      });
      return;
    }

    for (let i = start; i < n; i++) {
      const tr = rows[i];
      const td = tr.querySelector(`td[data-col-key="${escCss(ROW_HANDLE_KEY)}"]`);
      const badge = td ? td.querySelector(".te-row-number") : null;
      if (badge) badge.textContent = String(i + 1);
    }
  }

  // ---------------------------------------------------------
  // Selection visuals MUST be re-applied after render.js replaces DOM.
  // We coalesce to one microtask to avoid per-row overhead during bulk renders.
  // ---------------------------------------------------------
  let _selRefreshPending = false;

  function scheduleSelectionRefresh() {
    if (!spreadsheetEnabled) return;
    if (_selRefreshPending) return;
    _selRefreshPending = true;

    const run = () => {
      _selRefreshPending = false;
      try {
        ctx.applySelectionVisuals?.();
      } catch {}
    };

    if (typeof queueMicrotask === "function") queueMicrotask(run);
    else Promise.resolve().then(run);
  }

  function clearFrozenColumns() {
    table.classList.remove("te-freeze-cols");
    table.querySelectorAll("[data-te-frozen-col]").forEach((el) => {
      delete el.dataset.teFrozenCol;
      el.style.removeProperty("left");
      el.style.removeProperty("z-index");
      el.style.removeProperty("position");
    });
  }

  function applyFrozenColumns() {
    if (!freezeLeftCols || !cols.length) {
      clearFrozenColumns();
      return;
    }

    const visibleIdx = [];
    for (let i = 0; i < cols.length; i += 1) {
      const c = cols[i];
      if (!c?.key) continue;
      if (hiddenCols.has(c.key)) continue;
      visibleIdx.push(i);
    }
    if (!visibleIdx.length) {
      clearFrozenColumns();
      return;
    }

    const freezeIdx = visibleIdx.slice(0, Math.min(freezeLeftCols, visibleIdx.length));
    if (!freezeIdx.length) {
      clearFrozenColumns();
      return;
    }

    clearFrozenColumns();
    table.classList.add("te-freeze-cols");

    let leftPx = 0;
    for (const idx of freezeIdx) {
      const col = cols[idx];
      const key = String(col?.key || "");
      const th = table.querySelector(`thead th[data-col-idx="${String(idx)}"]`);
      const measured = Number(th?.getBoundingClientRect?.().width || 0);
      const width = Number.isFinite(measured) && measured > 0
        ? measured
        : (Number(col?.width) > 0 ? Number(col.width) : 96);
      const left = `${Math.max(0, Math.round(leftPx))}px`;

      table
        .querySelectorAll(
          `thead th[data-col-idx="${String(idx)}"],` +
          `tbody td[data-col-idx="${String(idx)}"],` +
          `tfoot td[data-col-idx="${String(idx)}"],` +
          `tfoot th[data-col-idx="${String(idx)}"]`,
        )
        .forEach((cell) => {
          cell.dataset.teFrozenCol = "1";
          cell.style.position = "sticky";
          cell.style.left = left;
          cell.style.zIndex = cell.closest("thead") ? "128" : "68";
        });

      leftPx += width;
    }
  }

  // ---------------------------------------------------------
  // Post-render sync (rowIdx + row badges + selection)
  // COALESCED: avoids O(n^2) during bulk renderRow calls
  // ---------------------------------------------------------
  let _postRenderPending = false;
  let _postRenderStartIdx = Number.POSITIVE_INFINITY;
  let _postRenderFull = false;

  function postRenderSync({ startIdx = 0, full = false } = {}) {
    // 1) row indices + row handles
    try { updateRowHandles({ startIdx, full }); } catch {}
    // 2) selection highlights (column/row/cell)
    scheduleSelectionRefresh();
    // 3) freeze panes (left columns) after DOM settles.
    try { applyFrozenColumns(); } catch {}
  }

  function schedulePostRenderSync({ startIdx = 0, full = false } = {}) {
    const at = Number.isFinite(startIdx) ? Math.max(0, Math.trunc(startIdx)) : 0;
    if (at < _postRenderStartIdx) _postRenderStartIdx = at;
    if (full) _postRenderFull = true;

    if (_postRenderPending) return;
    _postRenderPending = true;

    const run = () => {
      _postRenderPending = false;
      const at0 = Number.isFinite(_postRenderStartIdx) ? _postRenderStartIdx : 0;
      const full0 = _postRenderFull;
      _postRenderStartIdx = Number.POSITIVE_INFINITY;
      _postRenderFull = false;
      postRenderSync({ startIdx: at0, full: full0 });
    };

    if (typeof queueMicrotask === "function") queueMicrotask(run);
    else Promise.resolve().then(run);
  }

  // ---------------------------------------------------------
  // Virtual window mounting (opt-in)
  // ---------------------------------------------------------
  let _virtualMounted = { start: 0, end: -1 };
  let _virtualScrollHost = null;
  let _virtualMountPending = false;
  let _virtualMountAnchor = null;

  function activeCellAnchorRow() {
    const r = Number(ctx?._sel?.cellFocus?.r ?? ctx?._sel?.cellAnchor?.r);
    if (!Number.isInteger(r)) return null;
    return r;
  }

  function preferredVirtualAnchorRow(fallback = null) {
    const n = _allRows.length;
    if (!n) return 0;

    const active = activeCellAnchorRow();
    if (Number.isInteger(active)) {
      return Math.max(0, Math.min(n - 1, active));
    }

    if (Number.isInteger(fallback)) {
      return Math.max(0, Math.min(n - 1, fallback));
    }

    if (_virtualMounted.end >= _virtualMounted.start) {
      return Math.max(0, Math.min(n - 1, _virtualMounted.start));
    }

    return 0;
  }

  function virtualScrollHost() {
    if (!virtualEnabled) return null;
    if (_virtualScrollHost) return _virtualScrollHost;

    if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
      _virtualScrollHost = table.parentElement || null;
      return _virtualScrollHost;
    }

    let p = table.parentElement;
    while (p && p !== document.body) {
      try {
        const cs = window.getComputedStyle(p);
        const ov = `${cs.overflowY || ""} ${cs.overflow || ""}`;
        if (/(auto|scroll|overlay)/i.test(ov)) {
          _virtualScrollHost = p;
          return _virtualScrollHost;
        }
      } catch {}
      p = p.parentElement;
    }

    _virtualScrollHost = table.parentElement || null;
    return _virtualScrollHost;
  }

  function virtualViewportMetrics() {
    const host = virtualScrollHost();
    if (!host) return { scrollTop: 0, height: 1 };

    const bodyTop = Number.isFinite(tbody.offsetTop) ? tbody.offsetTop : 0;
    const scrollTop = Math.max(0, Number(host.scrollTop || 0) - bodyTop);
    const height = Math.max(1, Number(host.clientHeight || 1));
    return { scrollTop, height };
  }

  function virtualRange(anchorRow = null) {
    const n = _allRows.length;
    if (!n) return { start: 0, end: -1 };

    const { scrollTop, height } = virtualViewportMetrics();
    const visibleCount = Math.max(1, Math.ceil(height / virtualRowHeight));
    const keepCount = Math.min(n, visibleCount + (virtualOverscan * 2));
    const maxStart = Math.max(0, n - keepCount);

    let start = Math.max(
      0,
      Math.min(maxStart, Math.floor(scrollTop / virtualRowHeight) - virtualOverscan),
    );

    if (Number.isInteger(anchorRow)) {
      if (anchorRow < start) start = Math.max(0, anchorRow - virtualOverscan);
      if (anchorRow >= start + keepCount) {
        start = Math.min(maxStart, anchorRow - keepCount + 1 + virtualOverscan);
      }
    }

    const end = Math.min(n - 1, start + keepCount - 1);
    return { start, end };
  }

  function clearSelectionMarksInRow(tr) {
    if (!tr) return;
    tr.classList.remove("te-selected", "te-range", "te-active");
    tr.removeAttribute("aria-selected");
    try {
      delete tr.dataset.teSelected;
      delete tr.dataset.teRange;
      delete tr.dataset.teActive;
    } catch {}

    tr.querySelectorAll(".te-selected, .te-range, .te-active").forEach((el) => {
      el.classList.remove("te-selected", "te-range", "te-active");
    });
    tr.querySelectorAll("[data-te-selected], [data-te-range], [data-te-active]").forEach((el) => {
      delete el.dataset.teSelected;
      delete el.dataset.teRange;
      delete el.dataset.teActive;
    });
    tr.querySelectorAll("[aria-selected]").forEach((el) => el.removeAttribute("aria-selected"));
  }

  function makeVirtualSpacer(heightPx) {
    const h = Math.max(0, Math.ceil(heightPx));
    const tr = document.createElement("tr");
    tr.dataset.teVirtualSpacer = "1";
    tr.setAttribute("role", "presentation");
    tr.setAttribute("aria-hidden", "true");

    const td = document.createElement("td");
    td.colSpan = Math.max(1, cols.length);
    td.setAttribute("role", "presentation");
    td.setAttribute("aria-hidden", "true");
    td.style.height = `${h}px`;
    td.style.padding = "0";
    td.style.border = "0";
    td.style.lineHeight = "0";
    td.style.fontSize = "0";
    td.textContent = "";

    tr.appendChild(td);
    return tr;
  }

  function mountVirtualWindow({ force = false, anchorRow = null } = {}) {
    if (!virtualEnabled) return;

    const n = _allRows.length;
    if (!n) {
      tbody.innerHTML = "";
      _virtualMounted = { start: 0, end: -1 };
      return;
    }

    const next = virtualRange(anchorRow);
    if (!force && next.start === _virtualMounted.start && next.end === _virtualMounted.end) {
      return;
    }

    if (virtualRecycle) {
      if (_virtualMounted.end >= _virtualMounted.start && !force) {
        for (let i = _virtualMounted.start; i <= _virtualMounted.end; i++) {
          if (i < next.start || i > next.end) compactRow(_allRows[i]);
        }
      } else {
        for (let i = 0; i < n; i++) {
          if (i < next.start || i > next.end) compactRow(_allRows[i]);
        }
      }
    }

    const frag = document.createDocumentFragment();

    const topHeight = next.start * virtualRowHeight;
    if (topHeight > 0) frag.appendChild(makeVirtualSpacer(topHeight));

    for (let i = next.start; i <= next.end; i++) {
      const tr = _allRows[i];
      if (!tr) continue;
      if (virtualRecycle) materializeRow(tr, i);
      clearSelectionMarksInRow(tr);
      frag.appendChild(tr);
    }

    const bottomHeight = (n - next.end - 1) * virtualRowHeight;
    if (bottomHeight > 0) frag.appendChild(makeVirtualSpacer(bottomHeight));

    tbody.replaceChildren(frag);
    _virtualMounted = next;
    scheduleSelectionRefresh();
  }

  function scheduleVirtualWindow(anchorRow = null) {
    if (!virtualEnabled) return;

    if (Number.isInteger(anchorRow)) _virtualMountAnchor = anchorRow;
    if (_virtualMountPending) return;
    _virtualMountPending = true;

    const run = () => {
      _virtualMountPending = false;
      const anchor = _virtualMountAnchor;
      _virtualMountAnchor = null;
      mountVirtualWindow({ anchorRow: Number.isInteger(anchor) ? anchor : null });
    };

    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  function ensureRowVisible(rowIdx) {
    if (!virtualEnabled) return;
    if (!Number.isInteger(rowIdx) || rowIdx < 0 || rowIdx >= _allRows.length) return;

    if (rowIdx < _virtualMounted.start || rowIdx > _virtualMounted.end) {
      mountVirtualWindow({ force: true, anchorRow: rowIdx });
    }

    const tr = _allRows[rowIdx];
    if (!tr) return;
    if (virtualRecycle && tr.dataset?.teCompacted === "1") {
      materializeRow(tr, rowIdx);
      scheduleSelectionRefresh();
    }
    try {
      tr.scrollIntoView({ block: "nearest", inline: "nearest" });
    } catch {}
  }

  function removeRowsByIndex(rowIdxs = []) {
    const n = virtualEnabled ? _allRows.length : getRowEls().length;
    const idxs = [...new Set((rowIdxs || [])
      .filter((x) => Number.isInteger(x))
      .filter((i) => i >= 0 && i < n)
    )].sort((a, b) => b - a);

    if (!idxs.length) return 0;
    const removedRows = [];

    if (virtualEnabled) {
      idxs.forEach((i) => {
        const tr = _allRows[i];
        if (tr) removedRows.push(tr);
        _allRows.splice(i, 1);
      });
      const anchor = Math.max(0, Math.min(_allRows.length - 1, idxs[idxs.length - 1] || 0));
      mountVirtualWindow({
        force: true,
        anchorRow: preferredVirtualAnchorRow(Number.isFinite(anchor) ? anchor : 0),
      });
    } else {
      const rows = getRowEls();
      idxs.forEach((i) => {
        const tr = rows[i];
        if (tr) removedRows.push(tr);
        tr?.remove?.();
      });
    }

    if (removedRows.length) {
      if (typeof ctx.markTotalsRowsRemoved === "function") {
        ctx.markTotalsRowsRemoved(removedRows);
      } else {
        ctx.markTotalsDirtyAll?.();
      }
    }

    const start = idxs[idxs.length - 1] || 0;
    schedulePostRenderSync({ startIdx: start });
    scheduleNotifyRendered?.();
    return idxs.length;
  }

  if (virtualEnabled) {
    const host = virtualScrollHost();
    if (host) on(host, "scroll", () => scheduleVirtualWindow(), { passive: true });
    if (typeof window !== "undefined") {
      on(window, "resize", () => scheduleVirtualWindow(preferredVirtualAnchorRow()), { passive: true });
    }
  }

  // ---------------------------------------------------------
  // header + colgroup
  // ---------------------------------------------------------
  function renderColgroup() {
    let cg = table.querySelector("colgroup");
    if (cg) cg.remove();

    cg = document.createElement("colgroup");
    cols.forEach((c) => {
      const colEl = document.createElement("col");
      colEl.dataset.colKey = c.key;

      const w = c.width;
      if (Number.isFinite(w)) colEl.style.width = `${w}px`;
      else if (typeof w === "string" && w.trim() !== "") colEl.style.width = w;

      if (hiddenCols.has(c.key)) colEl.style.display = "none";
      cg.appendChild(colEl);
    });

    table.insertBefore(cg, table.firstChild);
    refreshGridAriaCounts();

    // ✅ IMPORTANT: DOM changed; keep visuals in sync (coalesced)
    schedulePostRenderSync();
  }

  function renderHeader() {
    thead.innerHTML = "";
    const tr = document.createElement("tr");
    if (gridRoleEnabled) {
      tr.setAttribute("role", "row");
      tr.setAttribute("aria-rowindex", "1");
    }

    cols.forEach((c, idx) => {
      const th = document.createElement("th");
      th.textContent = c.header || c.key;
      if (gridRoleEnabled) {
        th.setAttribute("role", "columnheader");
        th.setAttribute("aria-colindex", String(idx + 1));
        th.setAttribute("aria-readonly", "true");
      }

      // ✅ REQUIRED for selection + menus
      th.dataset.colKey = c.key;
      th.dataset.colIdx = String(idx);

      // ✅ Column styling hooks (clean, spec-driven)
      if (c.headerClass) th.classList.add(...String(c.headerClass).split(/\s+/).filter(Boolean));
      if (c.cellClass) th.classList.add(...String(c.cellClass).split(/\s+/).filter(Boolean)); // header shares col class

      // ✅ headerAlign takes precedence; falls back to align
      const headerAlign = c.headerAlign ?? c.align;
      if (headerAlign) th.style.textAlign = String(headerAlign);

      th.title = "";

      if (isNumericCol?.(c)) {
        const fmt = effectiveFormatForCol?.(c);
        const base = normalizeFormat?.(c);
        const ov = getDpOverride?.(c.key);

        if (ov !== null && fmt?.userDp && !fmt?.int) {
          th.dataset.dpOverride = String(ov);
          th.title =
            `${th.textContent}\nDecimals override: ${ov} (base ${base?.dp})\nRight-click -> Decimals...`;
        } else {
          delete th.dataset.dpOverride;
        }
      }

      if (c.kind === "rowHandle") {
        th.style.cursor = "default";
        th.title = th.title || "Row actions";
        if (rowMenuHoverEnabled) {
          const trigger = document.createElement("button");
          trigger.type = "button";
          trigger.className = "te-row-menu-trigger";
          trigger.textContent = "\u22EE";
          trigger.setAttribute("aria-label", "Insert row");
          th.appendChild(trigger);
        }
      } else if (!spreadsheetEnabled) {
        th.style.cursor = "default";
      } else {
        th.style.cursor = "pointer";
        th.title =
          th.title ||
          (isLockedCol(c)
            ? "Protected column (locked). You can select/copy, but not edit/paste/clear/hide."
            : "Click to select. Shift/Ctrl for multi-select. Cmd/Ctrl+Arrow jumps to edge. Right-click for menu.");
      }

      const canReorder = !!ctx.isColumnReorderEnabledForKey?.(c.key);
      if (canReorder) {
        th.draggable = true;
        th.classList.add("te-col-draggable");
      } else {
        th.draggable = false;
        th.classList.remove("te-col-draggable");
      }

      const canResize = !!ctx.isColumnResizeEnabledForKey?.(c.key);
      if (canResize) {
        const grip = document.createElement("span");
        grip.className = "te-col-resizer";
        grip.dataset.colKey = c.key;
        grip.setAttribute("aria-hidden", "true");
        th.appendChild(grip);
      }

      if (hiddenCols.has(c.key)) {
        th.style.display = "none";
        if (gridRoleEnabled) th.setAttribute("aria-hidden", "true");
      } else if (gridRoleEnabled) {
        th.removeAttribute("aria-hidden");
      }
      tr.appendChild(th);
    });

    thead.appendChild(tr);
    refreshGridAriaCounts();

    // ✅ IMPORTANT: thead nodes were replaced (coalesced)
    schedulePostRenderSync();
  }

  // ---------------------------------------------------------
  // cell factories
  // ---------------------------------------------------------
  function resolveInputRenderSpec(col, item = null) {
    const type0 = String(col?.type || "text").toLowerCase();
    const isDateType = type0 === "date";

    const cellFmt = item && item.__format && item.__format[col.key];
    const colFmt = col?.format && typeof col.format === "object" ? col.format : null;
    const mergedFmt =
      cellFmt && typeof cellFmt === "object"
        ? { ...(colFmt || {}), ...cellFmt }
        : (colFmt || {});
    const colWithFmt = { ...(col || {}), format: mergedFmt };

    const fmtForType =
      ctx.effectiveFormatForCol?.(colWithFmt) ||
      ctx.normalizeFormat?.(colWithFmt) ||
      mergedFmt;

    const seps = ctx.resolveNumberSeparators?.(fmtForType) || { decimal: ".", group: "," };
    const wantsGrouping = !!(fmtForType?.group || fmtForType?.thousands);
    const wantsLocaleDecimal = seps.decimal !== ".";

    const isNumericType = type0 === "number";
    const nativeDateInput = isDateType && col?.nativeDateInput !== false;
    const needsTextBackedNumeric = isNumericType && (wantsGrouping || wantsLocaleDecimal);
    const resolvedInputType = isNumericType
      ? (needsTextBackedNumeric ? "text" : (type0 || "text"))
      : (isDateType ? (nativeDateInput ? "date" : "text") : (type0 || "text"));

    return {
      type0,
      cellFmt,
      colWithFmt,
      isNumericType,
      isDateType,
      inputType: resolvedInputType,
    };
  }

  function applyNumericCellFmtDataset(dataset, cellFmt) {
    if (!dataset) return;
    if (cellFmt && typeof cellFmt === "object") {
      dataset.teCellFmt = "1";
      delete dataset.teAutoFormat;

      if (cellFmt.int === true) {
        dataset.int = "1";
        delete dataset.dp;
      } else if (Number.isFinite(cellFmt.dp)) {
        dataset.dp = String(clampInt(cellFmt.dp, 0, 12));
        delete dataset.int;
      }

      if (cellFmt.strip !== undefined) {
        dataset.strip = cellFmt.strip ? "1" : "0";
      }
      if (cellFmt.group !== undefined || cellFmt.thousands !== undefined) {
        const on = cellFmt.group === true || cellFmt.thousands === true;
        dataset.group = on ? "1" : "0";
      }
      if (cellFmt.locale !== undefined) {
        const v = String(cellFmt.locale || "").trim();
        if (v) dataset.locale = v;
        else delete dataset.locale;
      }
      if (cellFmt.decimalSep !== undefined || cellFmt.decimal !== undefined) {
        const v = String(cellFmt.decimalSep ?? cellFmt.decimal ?? "").trim();
        if (v) dataset.decimalSep = v;
        else delete dataset.decimalSep;
      }
      if (
        cellFmt.groupSep !== undefined ||
        cellFmt.thousandSep !== undefined ||
        cellFmt.thousandsSep !== undefined
      ) {
        const v = String(cellFmt.groupSep ?? cellFmt.thousandSep ?? cellFmt.thousandsSep ?? "").trim();
        if (v) dataset.groupSep = v;
        else delete dataset.groupSep;
      }
    } else {
      dataset.teAutoFormat = "1";
    }
  }

  function setDateInputValue(inputEl, col, value, { allowRawFallback = true } = {}) {
    if (!inputEl) return "";
    const opts = dateParseOptionsForCol(col);
    const raw = String(value ?? "").trim();
    if (raw === "") {
      inputEl.value = "";
      return "";
    }

    const iso = normalizeDateValue(raw, opts);
    if (iso) {
      inputEl.value = iso;
      return iso;
    }

    // input[type=date] silently clears invalid values.
    if (allowRawFallback && String(inputEl.type || "").toLowerCase() !== "date") {
      inputEl.value = raw;
      return raw;
    }

    inputEl.value = "";
    return "";
  }

  function normalizeDateInputElement(inputEl, col, { keepInvalidText = true } = {}) {
    if (!inputEl) return "";
    const raw = String(inputEl.value ?? "").trim();
    if (raw === "") {
      try {
        inputEl.setCustomValidity?.("");
      } catch {}
      inputEl.value = "";
      return "";
    }

    const opts = dateParseOptionsForCol(col);
    const iso = normalizeDateValue(raw, opts);
    if (iso) {
      if (inputEl.value !== iso) inputEl.value = iso;
      try {
        inputEl.setCustomValidity?.("");
      } catch {}
      return iso;
    }

    try {
      inputEl.setCustomValidity?.("Use YYYY-MM-DD.");
    } catch {}

    if (String(inputEl.type || "").toLowerCase() === "date") {
      inputEl.value = "";
      return "";
    }

    if (!keepInvalidText) inputEl.value = "";
    return keepInvalidText ? raw : "";
  }

  function makeInput(col, value, item) {
    const spec = resolveInputRenderSpec(col, item);
    const { type0, cellFmt, colWithFmt, isNumericType, isDateType, inputType } = spec;
    const v = value ?? col.default ?? "";

    // SELECT
    if (type0 === "select") {
      const sel = document.createElement("select");
      sel.className = "compact-input";
      sel.dataset.field = col.key;
      sel.dataset.colKey = col.key;

      const opts = Array.isArray(col.options) ? col.options : [];
      opts.forEach((o) => {
        const opt = document.createElement("option");
        if (o && typeof o === "object") {
          opt.value = o.value == null ? "" : String(o.value);
          opt.textContent = o.label == null ? String(opt.value) : String(o.label);
        } else {
          opt.value = o == null ? "" : String(o);
          opt.textContent = opt.value;
        }
        sel.appendChild(opt);
      });

      const s = v == null ? "" : String(v);
      sel.value = s;

      // add unknown value if not present
      if (s !== "" && sel.value !== s) {
        const opt = document.createElement("option");
        opt.value = s;
        opt.textContent = s;
        sel.appendChild(opt);
        sel.value = s;
      }

      if (col.readonly) sel.disabled = true;
      if (isLockedCol(col)) sel.disabled = true;

      hookProgrammaticValueAutosize?.(sel, col.key);
      return sel;
    }

    // INPUT
    const inp = document.createElement("input");
    inp.className = "compact-input";

    inp.dataset.field = col.key;
    inp.dataset.colKey = col.key;

    // ✅ ID must never be treated as numeric (no dp/int/strip formatting, no numeric formatter)
    // Also protects you if the spec accidentally sets type:"number" for identifier.
    if (col.key === "identifier" || col.key === "id") {
      inp.type = "text";

      // remove any numeric formatting hints that a formatter might pick up
      try {
        delete inp.dataset.dp;
        delete inp.dataset.int;
        delete inp.dataset.strip;
        delete inp.dataset.raw;
        delete inp.dataset.teAutoFormat;
        delete inp.dataset.teCellFmt;
      } catch {}

      // write as plain string
      inp.value = v == null ? "" : String(v);

      if (col.placeholder) inp.placeholder = col.placeholder;
      if (col.readonly) inp.readOnly = true;
      if (isLockedCol(col)) inp.readOnly = true;

      hookProgrammaticValueAutosize?.(inp, col.key);
      return inp;
    }

    inp.type = inputType;

    if (isNumericType) {
      inp.dataset.teNumeric = "1";
      if (inp.type === "text") inp.setAttribute("inputmode", "decimal");

      applyNumericCellFmtDataset(inp.dataset, cellFmt);

      setNumericInputValue?.(inp, colWithFmt, v);
    } else if (isDateType) {
      inp.dataset.teDate = "1";
      if (inp.type === "text") inp.setAttribute("inputmode", "numeric");

      setDateInputValue(inp, col, v, { allowRawFallback: true });

      const minIso = normalizeDateValue(col?.minDate ?? col?.min, dateParseOptionsForCol(col));
      if (minIso) inp.min = minIso;
      const maxIso = normalizeDateValue(col?.maxDate ?? col?.max, dateParseOptionsForCol(col));
      if (maxIso) inp.max = maxIso;
    } else {
      inp.value = v == null ? "" : String(v);
    }

    if (col.placeholder) inp.placeholder = col.placeholder;
    if (col.readonly) inp.readOnly = true;
    if (isLockedCol(col)) inp.readOnly = true;

    if (isNumericType) {
      on(inp, "input", (e) => {
        // Keep canonical raw during programmatic/synthetic updates (undo/redo, scripted fills).
        if (e && e.isTrusted === false) return;
        clearRawDatasets?.(inp);
      });
    } else if (isDateType) {
      const normalizeDateOnChange = () => {
        normalizeDateInputElement(inp, col, { keepInvalidText: inp.type !== "date" });
      };
      on(inp, "change", normalizeDateOnChange);
      on(inp, "blur", normalizeDateOnChange);
    }

    hookProgrammaticValueAutosize?.(inp, col.key);
    return inp;
  }

  function makeOutput(col, value) {
    const sp = document.createElement("span");
    sp.className = "cell-output";
    sp.dataset.out = col.key;
    sp.dataset.colKey = col.key;

    const v = value ?? "N/A";

    if (col && isNumericCol?.(col) && v !== "N/A") {
      setNumericOutputValue?.(sp, col, v);
    } else {
      sp.textContent = col?.format ? colFormat(col, v) : String(v);
    }

    if (isLockedCol(col)) sp.dataset.locked = "1";
    return sp;
  }

  function makeActions(col) {
    const wrap = document.createElement("div");
    wrap.className = "cell-actions";

    (col.actions || []).forEach((a) => {
      if (a === "deleteRow") {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "delete-btn";
        b.dataset.action = "deleteRow";
        b.textContent = "❌";
        b.title = "Delete row";
        wrap.appendChild(b);
      } else {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "action-btn";
        b.dataset.action = String(a);
        b.textContent = String(a);
        wrap.appendChild(b);
      }
    });

    return wrap;
  }

  function makeRowHandleCell() {
    const wrap = document.createElement("div");
    wrap.className = "te-row-handle";
    wrap.style.display = "flex";
    wrap.style.alignItems = "center";
    wrap.style.justifyContent = "center";
    wrap.style.gap = "6px";

    const badge = document.createElement("span");
    badge.className = "te-row-number";
    badge.textContent = "";
    wrap.appendChild(badge);

    if (rowMenuHoverEnabled) {
      const trigger = document.createElement("button");
      trigger.type = "button";
      trigger.className = "te-row-menu-trigger";
      trigger.textContent = "\u22EE";
      trigger.setAttribute("aria-label", "Row menu");
      wrap.appendChild(trigger);
    }

    return wrap;
  }

  // item.__cell = { [colKey]: { className?: "a b", style?: { ... } } }
  function populateRowCells(tr, item = {}) {
    if (!tr) return;
    tr.replaceChildren();
    if (gridRoleEnabled) tr.setAttribute("role", "row");
    cols.forEach((col, idx) => {
      const td = document.createElement("td");
      if (gridRoleEnabled) {
        td.setAttribute("role", col.kind === "rowHandle" ? "rowheader" : "gridcell");
        td.setAttribute("aria-colindex", String(idx + 1));
        const rowReadOnly = item?.readonly === true;
        const cellReadOnly =
          rowReadOnly ||
          col.kind !== "input" ||
          !!col.readonly ||
          isLockedCol(col);
        td.setAttribute("aria-readonly", cellReadOnly ? "true" : "false");
      }

      // ✅ REQUIRED for selection math + visuals
      td.dataset.colKey = col.key;
      td.dataset.colIdx = String(idx);

      if (hiddenCols.has(col.key)) {
        td.style.display = "none";
        if (gridRoleEnabled) td.setAttribute("aria-hidden", "true");
      } else if (gridRoleEnabled) {
        td.removeAttribute("aria-hidden");
      }
      if (isLockedCol(col)) td.dataset.locked = "1";

      // ✅ Column styling hooks
      if (col.cellClass) td.classList.add(...String(col.cellClass).split(/\s+/).filter(Boolean));

      // ✅ cellAlign takes precedence; falls back to align
      const cellAlign = col.cellAlign ?? col.align;
      if (cellAlign) td.style.textAlign = String(cellAlign);

      // ✅ Optional per-row/per-cell overrides
      const cellOv = item && item.__cell && item.__cell[col.key];
      if (cellOv && typeof cellOv === "object") {
        if (cellOv.className) td.classList.add(...String(cellOv.className).split(/\s+/).filter(Boolean));
        if (cellOv.style && typeof cellOv.style === "object") Object.assign(td.style, cellOv.style);
        if (cellOv.title != null) td.title = String(cellOv.title);
      }

      if (col.kind === "rowHandle") {
        td.appendChild(makeRowHandleCell());
        tr.appendChild(td);
        return;
      }

      if (col.kind === "input") {
        const v = item?.[col.key] ?? col.default ?? "";
        td.appendChild(makeInput(col, v, item));

        if (item?.readonly === true) {
          const inp = td.querySelector("input,textarea,select");
          if (inp) {
            if (inp.tagName === "SELECT") inp.disabled = true;
            else inp.readOnly = true;
          }
        }

        tr.appendChild(td);
        return;
      }

      if (col.kind === "output") {
        const v = item?.[col.key] ?? "N/A";
        td.appendChild(makeOutput(col, v));
        tr.appendChild(td);
        return;
      }

      if (col.kind === "actions") {
        td.appendChild(makeActions(col));
        tr.appendChild(td);
        return;
      }

      td.textContent = item?.[col.key] ?? "";
      tr.appendChild(td);
    });
  }

  // item.__cell = { [colKey]: { className?: "a b", style?: { ... } } }
  function renderRow(item = {}, { insertAt = null, mount = true } = {}) {
    const tr = document.createElement("tr");
    tr.dataset.teRow = "1";
    if (gridRoleEnabled) tr.setAttribute("role", "row");

    if (spec.rowKey) {
      const idVal = item?.[spec.rowKey];
      tr.dataset.rowId = idVal == null ? "" : String(idVal);
    }

    _rowMeta.set(tr, {
      readonly: item?.readonly === true,
      cell: item && item.__cell && typeof item.__cell === "object" ? item.__cell : null,
      format: item && item.__format && typeof item.__format === "object" ? item.__format : null,
    });

    const compactOnly = virtualEnabled && virtualRecycle && !mount;
    if (compactOnly) seedCompactedRowFromItem(tr, item);
    else populateRowCells(tr, item);

    if (virtualEnabled) {
      const rowCount = _allRows.length;
      const at =
        (insertAt == null || insertAt < 0 || insertAt >= rowCount) ? rowCount : insertAt;
      _allRows.splice(at, 0, tr);

      if (virtualRecycle && !mount && tr.dataset?.teCompacted !== "1") compactRow(tr);

      if (mount) {
        // Structural row changes need fresh row indices for selection/history.
        if (typeof ctx.markTotalsRowsInserted === "function") {
          ctx.markTotalsRowsInserted([tr]);
        } else {
          ctx.markTotalsDirtyAll?.();
        }
        schedulePostRenderSync({ startIdx: at });
        mountVirtualWindow({ force: true, anchorRow: at });
        scheduleAutoWidth?.();
        scheduleNotifyRendered?.();
      }
      return tr;
    }

    if (mount) {
      const rowCount = tbody?.rows ? tbody.rows.length : tbody.children.length;
      const at =
        (insertAt == null || insertAt < 0 || insertAt >= rowCount) ? rowCount : insertAt;
      if (at === rowCount) {
        tbody.appendChild(tr);
      } else {
        const rows = tbody?.rows ? tbody.rows : tbody.children;
        tbody.insertBefore(tr, rows[at] || null);
      }

      // ✅ IMPORTANT: new row nodes exist; keep rowIdx + selection in sync (coalesced)
      if (typeof ctx.markTotalsRowsInserted === "function") {
        ctx.markTotalsRowsInserted([tr]);
      } else {
        ctx.markTotalsDirtyAll?.();
      }
      schedulePostRenderSync({ startIdx: at });

      scheduleAutoWidth?.();
      scheduleNotifyRendered?.();
    }
    return tr;
  }

  function clearRows() {
    if (virtualEnabled) {
      _allRows = [];
      _virtualMounted = { start: 0, end: -1 };
      _rowSnapshots = new WeakMap();
      _rowMeta = new WeakMap();
    }
    ctx.markTotalsDirtyAll?.();
    tbody.innerHTML = "";

    // ✅ IMPORTANT: removing rows should clear painted selection and reset indices (coalesced)
    schedulePostRenderSync({ full: true });

    scheduleNotifyRendered?.();
  }

  // ---------------------------------------------------------
  // read/write items
  // ---------------------------------------------------------
  function readRow(tr, opts) {
    const includeOutputs = !!(opts && opts.includeOutputs);

    const out = {};
    cols.forEach((c) => {
      if (c.kind !== "input") return;
      const el = getInput(tr, c.key);
      if (!el) return;

      if (c.type === "number") {
        const fmt = effectiveFormatForCol?.(c) || {};
        const sc = colScale?.(c) ?? 1;

        if (String(el.value ?? "").trim() === "") {
          if (fmt.blankValue !== undefined) out[c.key] = fmt.blankValue;
          else out[c.key] = "";
          return;
        }

        const raw = String(getAnyRawDataset?.(el) ?? "").trim();
        let nCanon = NaN;

        if (raw !== "") {
          const n0 = parseNum(raw);
          if (Number.isFinite(n0)) nCanon = n0;
        } else {
          const nDisp = parseNum(el.value, fmt);
          if (Number.isFinite(nDisp)) {
            nCanon = nDisp / sc;
            el.dataset.raw = String(nCanon);
          }
        }

        if (!Number.isFinite(nCanon)) {
          out[c.key] = "";
          return;
        }

        if (fmt.roundPayload) {
          const disp = nCanon * sc;
          const dispR = fmt.int ? Math.round(disp) : Number(disp.toFixed(fmt.dp));
          out[c.key] = dispR / sc;
        } else {
          out[c.key] = nCanon;
        }
        return;
      }

      if (isDateCol(c)) {
        const raw = String(el.value ?? "").trim();
        if (raw === "") {
          out[c.key] = "";
          return;
        }
        const iso = normalizeDateValue(raw, dateParseOptionsForCol(c));
        out[c.key] = iso || raw;
        return;
      }

      out[c.key] = el.value ?? "";
    });

    if (includeOutputs) {
      for (const col of cols) {
        if (!col || col.kind !== "output" || !col.key) continue;
        const el = getOut(tr, col.key);
        if (!el) continue;

        const raw = (el.dataset && el.dataset.raw != null) ? String(el.dataset.raw).trim() : "";
        if (raw !== "") {
          const fmt = effectiveFormatForCol?.(col);
          const n = parseNum(raw);
          out[col.key] = Number.isFinite(n) ? n : raw;
        } else {
          const txt = String(el.textContent || "").trim();
          if (txt === "" || txt === "N/A") {
            out[col.key] = "";
            continue;
          }

          if (isNumericCol?.(col)) {
            const fmt = effectiveFormatForCol?.(col);
            const sc = colScale?.(col) ?? 1;
            const nDisp = parseNum(txt, fmt);
            out[col.key] = Number.isFinite(nDisp) ? (nDisp / sc) : txt;
          } else {
            out[col.key] = txt;
          }
        }
      }
    }

    return out;
  }

  function readAll(opts) {
    return getRowEls().map((tr) => readRow(tr, opts));
  }

  function getItems() {
    return readAll({});
  }

  function stableSig(value, seen = new WeakSet()) {
    if (value === null || value === undefined) return "null";
    const t = typeof value;
    if (t === "number") {
      if (Number.isNaN(value)) return "num:NaN";
      if (!Number.isFinite(value)) return value > 0 ? "num:+Inf" : "num:-Inf";
      return `num:${String(value)}`;
    }
    if (t === "boolean") return value ? "bool:1" : "bool:0";
    if (t === "string") return `str:${value}`;
    if (t !== "object") return `${t}:${String(value)}`;

    if (seen.has(value)) return "[Circular]";
    seen.add(value);

    if (Array.isArray(value)) {
      const arrSig = `[${value.map((v) => stableSig(v, seen)).join(",")}]`;
      seen.delete(value);
      return arrSig;
    }

    const keys = Object.keys(value).sort();
    const body = keys.map((k) => `${k}:${stableSig(value[k], seen)}`).join(",");
    seen.delete(value);
    return `{${body}}`;
  }

  function normalizeRowKeyValue(value) {
    if (value == null) return "";
    const s = String(value).trim();
    return s;
  }

  function rowKeyForItem(item, rowKey) {
    if (!rowKey || !item || typeof item !== "object") return "";
    return normalizeRowKeyValue(item[rowKey]);
  }

  function rowKeyForTr(tr, rowKey) {
    if (!rowKey || !tr) return "";

    const ds = normalizeRowKeyValue(tr.dataset?.rowId);
    if (ds !== "") return ds;

    const snap = rowSnapshotToItem(tr);
    return rowKeyForItem(snap, rowKey);
  }

  function hasUniqueIncomingKeys(items, rowKey) {
    const seen = new Set();
    for (const item of items || []) {
      const k = rowKeyForItem(item, rowKey);
      if (!k) return false;
      if (seen.has(k)) return false;
      seen.add(k);
    }
    return true;
  }

  function collectExistingRowsByKey(rowKey) {
    const byKey = new Map();
    const rows = virtualEnabled ? _allRows : getRowEls();

    rows.forEach((tr) => {
      const k = rowKeyForTr(tr, rowKey);
      if (!k) return;
      let bucket = byKey.get(k);
      if (!bucket) {
        bucket = [];
        byKey.set(k, bucket);
      }
      bucket.push(tr);
    });
    return byKey;
  }

  function applyRowMeta(tr, item = {}) {
    if (!tr) return;
    _rowMeta.set(tr, {
      readonly: item?.readonly === true,
      cell: item && item.__cell && typeof item.__cell === "object" ? item.__cell : null,
      format: item && item.__format && typeof item.__format === "object" ? item.__format : null,
    });

    if (spec.rowKey) {
      const idVal = item?.[spec.rowKey];
      tr.dataset.rowId = idVal == null ? "" : String(idVal);
    }
  }

  function rowSignatureFromItem(item = {}) {
    const payload = Object.create(null);
    cols.forEach((col) => {
      if (!col?.key) return;
      if (col.kind === "input") payload[`in:${col.key}`] = item?.[col.key] ?? col.default ?? "";
      if (col.kind === "output") payload[`out:${col.key}`] = item?.[col.key] ?? "N/A";
    });
    payload.readonly = item?.readonly === true;
    payload.__cell = item && item.__cell && typeof item.__cell === "object" ? item.__cell : null;
    payload.__format = item && item.__format && typeof item.__format === "object" ? item.__format : null;
    return stableSig(payload);
  }

  function rowSignatureFromTr(tr) {
    if (!tr) return "";
    return rowSignatureFromItem(rowSnapshotToItem(tr));
  }

  function patchRowFromItem(tr, item = {}) {
    if (!tr) return;
    applyRowMeta(tr, item);
    if (virtualRecycle && tr.dataset?.teCompacted === "1") {
      seedCompactedRowFromItem(tr, item);
      return;
    }

    populateRowCells(tr, item);
    _rowSnapshots.delete(tr);
    try {
      delete tr.dataset.teCompacted;
    } catch {}
  }

  function setItemsFullReplace(items, opts = {}) {
    const arr = Array.isArray(items) ? items : [];
    const doTotals = opts?.totals !== false;
    const doReformat = opts?.reformat !== false;
    const doAutoWidth = opts?.autoWidth !== false;
    const doPostSync = opts?.postSync !== false;

    // ensure we fire ONE te:render when bulk loading
    setSuppressRenderNotify?.(true);
    if (doTotals) ctx.markTotalsDirtyAll?.();

    clearRows();

    if (virtualEnabled) {
      _allRows = [];
      if (arr.length) {
        arr.forEach((it) => {
          renderRow(it, { mount: false });
        });
      }
      mountVirtualWindow({ force: true, anchorRow: preferredVirtualAnchorRow(0) });
    } else if (arr.length) {
      // Build rows off-DOM and append once to reduce layout/reflow work on big loads.
      const frag = document.createDocumentFragment();
      arr.forEach((it) => {
        const tr = renderRow(it, { mount: false });
        if (tr) frag.appendChild(tr);
      });
      tbody.appendChild(frag);
    }

    setSuppressRenderNotify?.(false);

    // totals + formatting + autosize
    if (doTotals) ctx.updateTotals?.();
    if (doReformat) {
      try { ctx.reformatAllNumeric?.(); } catch {}
    }
    if (doAutoWidth) {
      try { scheduleAutoWidth?.(); } catch {}
    }

    // ✅ IMPORTANT: after bulk set, do one immediate sync (rowIdx + badges + selection)
    if (doPostSync) postRenderSync();

    scheduleNotifyRendered?.();
    return arr;
  }

  function setItemsIncremental(items, opts = {}) {
    const arr = Array.isArray(items) ? items : [];
    const rowKey =
      (typeof opts?.rowKey === "string" && opts.rowKey.trim()) ||
      (typeof spec?.rowKey === "string" && spec.rowKey.trim()) ||
      "";
    if (!rowKey) return setItemsFullReplace(arr, opts);
    if (!hasUniqueIncomingKeys(arr, rowKey)) return setItemsFullReplace(arr, opts);

    const doTotals = opts?.totals !== false;
    const doReformat = opts?.reformat !== false;
    const doAutoWidth = opts?.autoWidth !== false;
    const doPostSync = opts?.postSync !== false;

    const existingRows = virtualEnabled ? _allRows.slice() : getRowEls();
    const existingByKey = collectExistingRowsByKey(rowKey);
    const nextRows = [];
    let hadStructureChange = false;
    let hadContentChange = false;

    arr.forEach((item) => {
      const key = rowKeyForItem(item, rowKey);
      const bucket = existingByKey.get(key);
      const tr = bucket && bucket.length ? bucket.shift() : null;
      if (bucket && !bucket.length) existingByKey.delete(key);

      if (!tr) {
        const created = renderRow(item, { mount: false });
        nextRows.push(created);
        hadStructureChange = true;
        return;
      }

      const prevSig = rowSignatureFromTr(tr);
      const nextSig = rowSignatureFromItem(item);
      if (prevSig !== nextSig) {
        patchRowFromItem(tr, item);
        hadContentChange = true;
      } else {
        applyRowMeta(tr, item);
      }
      nextRows.push(tr);
    });

    for (const bucket of existingByKey.values()) {
      if (bucket.length) {
        hadStructureChange = true;
        break;
      }
    }

    if (!hadStructureChange && existingRows.length !== nextRows.length) hadStructureChange = true;
    if (!hadStructureChange) {
      for (let i = 0; i < existingRows.length; i++) {
        if (existingRows[i] !== nextRows[i]) {
          hadStructureChange = true;
          break;
        }
      }
    }

    const changed = hadStructureChange || hadContentChange;
    if (!changed) {
      if (doPostSync) postRenderSync();
      scheduleNotifyRendered?.();
      return arr;
    }

    setSuppressRenderNotify?.(true);
    if (doTotals) ctx.markTotalsDirtyAll?.();

    if (virtualEnabled) {
      _allRows = nextRows;
      mountVirtualWindow({ force: true, anchorRow: preferredVirtualAnchorRow(0) });
    } else {
      const frag = document.createDocumentFragment();
      nextRows.forEach((tr) => {
        if (tr) frag.appendChild(tr);
      });
      tbody.replaceChildren(frag);
    }

    setSuppressRenderNotify?.(false);

    if (doTotals) ctx.updateTotals?.();
    if (doReformat) {
      try {
        ctx.reformatAllNumeric?.();
      } catch {}
    }
    if (doAutoWidth) {
      try {
        scheduleAutoWidth?.();
      } catch {}
    }
    if (doPostSync) postRenderSync();

    scheduleNotifyRendered?.();
    return arr;
  }

  function setItems(items, opts = {}) {
    const wantIncremental = opts?.incremental !== false;
    const keyOverride = typeof opts?.rowKey === "string" ? opts.rowKey.trim() : "";
    const specKey = typeof spec?.rowKey === "string" ? spec.rowKey.trim() : "";
    const canIncremental = wantIncremental && !!(keyOverride || specKey);

    if (canIncremental) return setItemsIncremental(items, opts);
    return setItemsFullReplace(items, opts);
  }

  function load(items, opts) {
    return setItems(items, opts);
  }

  // ---------------------------------------------------------
  // outputs
  // ---------------------------------------------------------
  function setOutput(tr, key, value) {
    const col = colByKey.get(key);
    const el = getOut(tr, key);
    if (!el) return;

    if (col && isNumericCol?.(col) && value !== "N/A") {
      setNumericOutputValue?.(el, col, value);
    } else if (col && col.format) {
      el.textContent = colFormat(col, value);
    } else {
      el.textContent = value == null ? "" : String(value);
    }

    const r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
    if (Number.isInteger(r) && r >= 0) ctx.markTotalsDirtyRow?.(r);
    else ctx.markTotalsDirtyAll?.();

    scheduleAutoWidth?.([key]);
  }

  function setOutputs(tr, obj) {
    if (!tr || !obj) return;
    Object.entries(obj).forEach(([k, v]) => setOutput(tr, k, v));
  }

  // ---------------------------------------------------------
  // expose to ctx
  // ---------------------------------------------------------
  ctx.renderColgroup = renderColgroup;
  ctx.renderHeader = renderHeader;

  ctx.makeInput = makeInput;
  ctx.makeOutput = makeOutput;
  ctx.makeActions = makeActions;

  ctx.renderRow = renderRow;
  ctx.clearRows = clearRows;

  ctx.getRowEls = getRowEls;
  ctx.getRenderedRowEls = getRenderedRowEls;
  ctx.updateRowHandles = updateRowHandles;
  ctx.removeRowsByIndex = removeRowsByIndex;
  ctx.ensureRowVisible = ensureRowVisible;
  ctx.isVirtualizedRows = () => virtualEnabled;

  ctx.getInput = getInput;
  ctx.getOut = getOut;
  ctx.getOutputRaw = getOutputRaw;

  ctx.readRow = readRow;
  ctx.readAll = readAll;
  ctx.snapshotRowItem = rowSnapshotToItem;
  ctx.getItems = getItems;
  ctx.setItems = setItems;
  ctx.load = load;

  ctx.setOutput = setOutput;
  ctx.setOutputs = setOutputs;

  // Convenience: allow engine.js to reformat by col key without duplicating loops
  ctx.reformatColumn = function reformatColumn(colKey) {
    const col = colByKey.get(colKey);
    if (!col || !isNumericCol?.(col)) return;

    if (col.kind === "input") {
      getRowEls().forEach((tr) => {
        const el = getInput(tr, colKey);
        if (el && el.tagName === "INPUT") reformatNumericInput?.(el, col);
      });
    }

    if (col.kind === "output") {
      getRowEls().forEach((tr) => {
        const el = getOut(tr, colKey);
        if (el) reformatNumericOutput?.(el, col);
      });
    }
  };

  // Expose (optional) hook for other modules if needed
  ctx._scheduleSelectionRefresh = scheduleSelectionRefresh;
  ctx._schedulePostRenderSync = schedulePostRenderSync;
  ctx.refreshGridAriaCounts = refreshGridAriaCounts;
  refreshGridAriaCounts();
}
